import Image from "next/image"
import Link from "next/link"
import MarketingHeading from "@/components/common/MarketingHeading"
import { PageMeta } from "@/components/common/Seo/PageMeta"
import { MarkdownContentRenderer } from "@/components/resources/components/MarkdownContentRenderer"
import { Pagination } from "@/components/resources/components/Pagination"
import type { ChangelogArchiveProps } from "@/components/resources/data/archives"
import { BASE_BLOG_PATH } from "@/constants/urls"
import { BlogImage } from "../components/BlogImage"
import { formatDate } from "../components/ui/utils"

const formatTimelineDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/** One page of the changelog timeline */
export const ChangelogCategoryArchive = ({
  meta,
  entries,
  title,
  pagination,
}: ChangelogArchiveProps) => {
  return (
    <article className="flex flex-col mb-16 mx-auto max-w-4xl">
      <PageMeta meta={meta} />
      {/* The changelog shows no visible title */}
      <h1 className="sr-only">{title}</h1>
      {entries.length ? (
        <div className="mt-16">
          <div className="relative">
            {/* Timeline entries */}
            <div className="flex flex-col gap-16">
              {entries.map((entry) => {
                const date = entry.publishedAt ? new Date(entry.publishedAt) : null

                return (
                  <div key={entry.id ?? entry.slug} className="relative flex gap-8">
                    {/* Date column - only show on desktop */}
                    <div className="w-[200px] shrink-0 pt-1 justify-start hidden sm:flex items-end h-(--text-3xl)">
                      {date ? (
                        <time
                          dateTime={formatDate(date)}
                          className="text-sm text-fg-secondary font-medium"
                        >
                          {formatTimelineDate(date)}
                        </time>
                      ) : null}
                    </div>
                    {/* Content column */}
                    <div className="flex-1 pb-8 pl-0 sm:pl-8 relative group">
                      {/* Date under the heading in mobile */}
                      {date ? (
                        <time
                          dateTime={formatDate(date)}
                          className="block sm:hidden text-sm text-fg-secondary mb-2"
                        >
                          {formatTimelineDate(date)}
                        </time>
                      ) : null}
                      <Link
                        href={`${BASE_BLOG_PATH}${entry.category ? `/${entry.category}` : ""}/${entry.slug}`}
                        className="block mb-3 sm:mb-5"
                      >
                        <MarketingHeading level={1} as="h2">
                          {entry.title}
                        </MarketingHeading>
                      </Link>
                      {typeof entry.image === "string" ? (
                        <Image
                          src={entry.image}
                          alt={entry.title}
                          className="w-full rounded-lg aspect-blog-cover object-cover object-center mb-8"
                          width={1200}
                          height={800}
                        />
                      ) : (
                        <BlogImage
                          width="413"
                          height="592"
                          alt={entry.title}
                          className="w-full rounded-lg aspect-blog-cover object-cover object-center mb-8"
                          {...{
                            category: entry.category,
                            image: entry.image,
                            releaseNumber: entry.releaseNumber,
                            projectMeta: entry.projectMeta,
                          }}
                        />
                      )}
                      <div className="changelog max-w-none [&_p]:mb-3 [&_p:last-child]:mb-0 relative z-2 [&_a]:pointer-events-auto *:pointer-events-none">
                        <MarkdownContentRenderer source={entry.mdxSource} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <Pagination {...pagination} />
          </div>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p>No posts in this category.</p>
        </div>
      )}
    </article>
  )
}
